<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">

            <!-- Pasek statusu -->
            <div class="mb-3 flex items-center justify-between">
              <div class="text-sm text-gray-600">
                <template v-if="isLoading">Ładowanie okresów… ({{periods.length}} znalezionych)</template>
                <template v-else>Załadowane okresy: {{periods.length}}</template>
              </div>
              <div class="flex gap-2">
                <button type="button" @click="loadFirstPage()" :disabled="isLoading"
                        class="px-3 py-1.5 rounded-md bg-slate-100 text-sm hover:bg-slate-200 disabled:opacity-50">
                  Odśwież
                </button>
                <button type="button" @click="loadMore()" :disabled="!canLoadMore || isLoading"
                        class="px-3 py-1.5 rounded-md bg-slate-100 text-sm hover:bg-slate-200 disabled:opacity-50">
                  Załaduj więcej
                </button>
              </div>
            </div>

            <div class="overflow-auto h-96">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Okres</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Ilość faktur</th>
                    <th class="relative py-3.5 pl-3 pr-4 sm:pr-3"></th>
                  </tr>
                </thead>
                <tbody class="bg-white">
                  <tr v-for="(row, idx) in periods" :key="row.key" :class="idx % 2 ? 'bg-gray-50' : ''">
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                      <div class="flex flex-col">
                        <span>{{ row.key }}</span>
                        <span v-if="row.parsed" class="text-xs text-gray-500">{{ row.parsed.startISO }} → {{ row.parsed.endISO }}</span>
                      </div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span v-if="row.meta.loading">…</span>
                      <span v-else-if="row.meta.count !== null">{{ row.meta.count }}</span>
                      <span v-else>—</span>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <input v-model="row.selected" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600" />
                    </td>
                  </tr>

                  <tr v-if="!periods.length">
                    <td colspan="3" class="py-6 text-center text-sm text-gray-500">Załaduj pierwszą paczkę okresów.</td>
                  </tr>
                </tbody>
              </table>

              <!-- Strażnik do infinite loading -->
              <div ref="sentinel" class="h-6 w-full"></div>
            </div>

            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button type="button"
                      :disabled="isGenerating || !hasAnySelected"
                      :class="['transition ease-in-out duration-300 bg-blue-600 text-base font-medium hover:bg-blue-700 focus:ring-blue-500',
                               'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50']"
                      @click.prevent="generateStatement()">
                <span v-if="!isGenerating">Generuj</span>
                <span v-else>Przetwarzanie…</span>
              </button>
              <button type="button"
                      class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      @click="close">
                Zamknij
              </button>
            </div>

          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, nextTick } from 'vue'
import { db } from "@/firebase/gfbconf.js";
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const DISCOVERY_BATCH = 300;      // paczka do odkrywania okresów
const COUNT_PAGE_SIZE = 1000;     // strona do zliczania dokumentów (doc length)
const COUNT_CONCURRENCY = 3;      // ile okresów liczymy równolegle
const FETCH_PAGE = 500;           // paczka do generowania PDF

export default {
  components: { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot },
  props: ["modalData"],
  setup() {
    const open = ref(true);
    const sentinel = ref(null);
    return { open, sentinel };
  },
  data() {
    return {
      periods: [],           // [{ key, meta:{count,loading}, firstUnix, selected, parsed }]
      isLoading: false,
      canLoadMore: false,
      lastDoc: null,
      isGenerating: false,
      countQueue: [],
      inflight: new Set(),
      counted: new Set(),
      countEpoch: 0,
      io: null
    }
  },
  computed: {
    hasAnySelected() { return this.periods.some(p => p.selected); },
    selectedPeriodKeys() { return this.periods.filter(p => p.selected).map(p => p.key); }
  },
  methods: {
    parsePeriod(str) {
      if (typeof str !== 'string') return null;
      const [a, b] = str.split(' - ');
      if (!a || !b) return null;
      const parseOne = (s) => {
        const [d,m,y] = s.split('-').map(n => parseInt(n,10));
        if (!d || !m || !y) return null;
        const pad = n => String(n).padStart(2,'0');
        return { d, m, y, iso: `${y}-${pad(m)}-${pad(d)}` };
      };
      const p1 = parseOne(a), p2 = parseOne(b);
      if (!p1 || !p2) return null;
      return {
        start: Date.UTC(p1.y, p1.m-1, p1.d, 0,0,0),
        end:   Date.UTC(p2.y, p2.m-1, p2.d, 23,59,59),
        startISO: p1.iso, endISO: p2.iso
      };
    },

    /* ------- Odkrywanie okresów -------- */
    async loadFirstPage() {
        // reset UI
        this.periods = [];
        this.lastDoc = null;

        // reset liczenia
        this.countQueue = [];
        this.inflight.clear();
        this.counted.clear();
        this.countEpoch++;       // <--- kluczowe: unieważnij stare workery

        // (opcjonalnie) zatrzymaj i odśwież IO
        this.detachObserver();

        await this.loadMore();

        // po pierwszym załadowaniu włącz IO znów
        this.attachObserver();
        },
    async loadMore(batch = DISCOVERY_BATCH) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        let q = db.collection("Invoices").orderBy("created.unix","desc").limit(batch);
        if (this.lastDoc) q = q.startAfter(this.lastDoc);
        const snap = await q.get();
        const docs = snap.docs;
        if (docs.length) this.lastDoc = docs[docs.length-1];

        const byKey = new Map(this.periods.map(p => [p.key, p]));
        const newly = [];

        for (const d of docs) {
          const inv = d.data();
          const period = inv?.period;
          if (!period) continue;
          if (!byKey.has(period)) {
            const obj = {
              key: period,
              meta: { count: null, loading: true },
              firstUnix: inv?.created?.unix ?? 0,
              selected: false,
              parsed: this.parsePeriod(period)
            };
            byKey.set(period, obj);
            newly.push(period);
          } else {
            const obj = byKey.get(period);
            const unix = inv?.created?.unix ?? 0;
            if (unix > obj.firstUnix) obj.firstUnix = unix;
          }
        }

        const arr = Array.from(byKey.values());
        arr.sort((A,B)=>{
          const a=A.parsed,b=B.parsed;
          if (a&&b){ if (b.start!==a.start) return b.start-a.start; if (b.end!==a.end) return b.end-a.end; return B.key.localeCompare(A.key); }
          if (A.firstUnix!==B.firstUnix) return B.firstUnix-A.firstUnix;
          return B.key.localeCompare(A.key);
        });

        this.periods = arr;
        this.canLoadMore = docs.length === batch;

        // uruchom doc-length liczenie liczników
        this.enqueueCounts(newly);
        this.kickCounters();

        await nextTick();
        this.attachObserver(); // włącz/odśwież IO (infinite)
      } catch (e) {
        console.error(e);
        alert('Nie udało się załadować okresów.');
      } finally {
        this.isLoading = false;
      }
    },

    /* ------- Kolejka liczników (doc length) ------- */
    enqueueCounts(keys){
        for (const k of keys){
            if (!this.counted.has(k)) {
            const row = this.periods.find(p => p.key === k);
            if (row) { row.meta.loading = true; row.meta.count = null; }
            this.countQueue.push(k);
            }
        }
    },
    async kickCounters(){
        const epoch = this.countEpoch;   // <--- zapamiętaj epokę dla tej serii workerów
        while (this.inflight.size < COUNT_CONCURRENCY && this.countQueue.length){
            const key = this.countQueue.shift();
            if (!key || this.inflight.has(key) || this.counted.has(key)) continue;
            this.inflight.add(key);

            this.countByPaging(key)
            .then(cnt => {
                // jeśli w międzyczasie było Odśwież (epoch się zmienił), ignoruj wynik
                if (epoch !== this.countEpoch) return;
                this.applyCount(key, cnt);
            })
            .catch(e => {
                if (epoch !== this.countEpoch) return;
                console.warn('countByPaging fail', key, e);
                this.applyCount(key, null);
            })
            .finally(()=>{
                this.inflight.delete(key);
                // jeśli epoka się zmieniła, nie odpalaj kolejnych z tej serii
                if (epoch !== this.countEpoch) return;
                if (this.countQueue.length) this.kickCounters();
            });
        }
        },
    async countByPaging(period, pageSize = COUNT_PAGE_SIZE){
      // iterujemy po dokumentach i zliczamy .size – nie pobieramy pełnych danych poza snapshotem
      let total = 0;
      let base = db.collection("Invoices").where("period","==",period).orderBy("created.unix","desc").limit(pageSize);
      let snap = await base.get();
      total += snap.size;
      while (snap.size === pageSize){
        const last = snap.docs[snap.docs.length-1];
        snap = await db.collection("Invoices")
              .where("period","==",period)
              .orderBy("created.unix","desc")
              .startAfter(last)
              .limit(pageSize)
              .get();
        total += snap.size;
      }
      return total;
    },
    applyCount(key, count){
      const row = this.periods.find(p => p.key===key);
      if (!row) return;
      row.meta.loading = false;
      row.meta.count = (typeof count === 'number') ? count : null; // null => „—”
      this.counted.add(key);
    },

    /* ------- Infinite loading ------- */
    attachObserver(){
      if (!this.$refs.sentinel || !('IntersectionObserver' in window)) return;
      if (this.io) this.io.disconnect();
      this.io = new IntersectionObserver((entries)=>{
        for (const e of entries){
          if (e.isIntersecting && this.canLoadMore && !this.isLoading){
            this.loadMore();
          }
        }
      }, { root: null, rootMargin: '0px', threshold: 0.1 });
      this.io.observe(this.$refs.sentinel);
    },
    detachObserver(){ if (this.io){ this.io.disconnect(); this.io=null; } },

    /* ------- Paged fetch do PDF ------- */
    async *fetchInvoicesForPeriod(period, pageSize = FETCH_PAGE){
      let q = db.collection("Invoices").where("period","==",period).orderBy("created.unix","desc").limit(pageSize);
      let snap = await q.get(); yield snap;
      while (!snap.empty && snap.size===pageSize){
        const last = snap.docs[snap.docs.length-1];
        snap = await db.collection("Invoices").where("period","==",period).orderBy("created.unix","desc").startAfter(last).limit(pageSize).get();
        yield snap;
      }
    },

    /* ------- Generowanie PDF (agregacja w locie) ------- */
    async generateStatement(){
      if (!this.hasAnySelected){ alert('Aby wygenerować zestawienie, musisz wybrać okres rozliczeniowy'); return; }
      this.isGenerating = true;
      try{
        const included = this.selectedPeriodKeys;
        const rows=[]; const idx=new Map(); const prodSum={};
        for (const p of included){
          for await (const snap of this.fetchInvoicesForPeriod(p, FETCH_PAGE)){
            snap.forEach(doc=>{
              const inv = doc.data();
              const client = inv.payerName;
              const txs = Array.isArray(inv.transactions)?inv.transactions:[];
              for (const t of txs){
                const k = `${client}|||${t.name}`;
                const qty = Number(t.quantity)||0;
                const gross = Number(t.grossValue)||0;
                if (idx.has(k)){ const i=idx.get(k); rows[i].quantity = Math.round((rows[i].quantity+qty)*100)/100; rows[i].grossValue = Math.round((rows[i].grossValue+gross)*100)/100; }
                else { idx.set(k, rows.length); rows.push({ clientName: client, productName: t.name, quantity: Math.round(qty*100)/100, grossValue: Math.round(gross*100)/100 }); }
                if (!prodSum[t.name]) prodSum[t.name] = { product:t.name, quantity:0, grossValue:0 };
                prodSum[t.name].quantity = Math.round((prodSum[t.name].quantity+qty)*100)/100;
                prodSum[t.name].grossValue = Math.round((prodSum[t.name].grossValue+gross)*100)/100;
              }
            });
          }
        }
        rows.sort((a,b)=>a.clientName.localeCompare(b.clientName,'pl',{sensitivity:'base'}));
        const summary = Object.values(prodSum).map(p=>({ ...p, quantity:+p.quantity.toFixed(2), grossValue:+p.grossValue.toFixed(2) }));

        const headerProducts = [
          { text:'Lp.', border:[false,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Klient', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Produkt', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Ilość (łącznie)', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Wartość brutto (łącznie)', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
        ];
        const productsTable=[headerProducts];
        rows.forEach((r,i)=>productsTable.push([
          { text:String(i+1), border:[false,true,true,true], margin:[-9,5,-5,5], alignment:'right', fontSize:8 },
          { text:r.clientName, border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
          { text:r.productName, border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
          { text:r.quantity.toFixed(2), border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
          { text:`${r.grossValue.toFixed(2)} PLN`, border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
        ]));

        const headerSummary = [
          { text:'Lp.', border:[false,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Produkt', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Ilość (łącznie)', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
          { text:'Wartość brutto (łącznie)', border:[true,true,true,true], alignment:'center', fillColor:'#eaf2f5', margin:[-9,5,-9,5], textTransform:'uppercase', fontSize:8 },
        ];
        const productsSummaryTable=[headerSummary];
        summary.forEach((p,i)=>productsSummaryTable.push([
          { text:String(i+1), border:[false,true,true,true], margin:[-9,5,-5,5], alignment:'right', fontSize:8 },
          { text:p.product, border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
          { text:p.quantity.toFixed(2), border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
          { text:`${p.grossValue.toFixed(2)} PLN`, border:[true,true,true,true], fillColor:'#f5f5f5', margin:[-9,5,-9,5], alignment:'center', fontSize:8 },
        ]));

        const doc = {
          pageSize:'A4', pageOrientation:'landscape',
          content:[
            { columns:[[ { text:'Zestawienie produktowe dla wszystkich klientów', color:'#333', width:'*', fontSize:16, bold:true, alignment:'center', margin:[0,10,0,10] } ]] },
            { columns:[ { text:'Zestawienie dla okresów:', color:'#aaa', bold:true, fontSize:12, alignment:'left', margin:[0,15,0,2] } ] },
            { columns:[ { text: this.selectedPeriodKeys.join('\n'), bold:true, fontSize:10, color:'#333', alignment:'left' } ] },
            '\n',
            { layout: this.tableLayout(), table:{ headerRows:1, dontBreakRows:true, widths:[1,'*',200,85,85], body:productsTable } },
            '\n','\n','\n',
            { columns:[[ { text:'Podsumowanie', color:'#333', width:'*', fontSize:16, bold:true, alignment:'center', margin:[0,10,0,10] } ]] },
            { layout: this.tableLayout(), table:{ headerRows:1, dontBreakRows:true, widths:[1,'*',120,120], body:productsSummaryTable } },
          ],
          defaultStyle:{ columnGap:20 }
        };

        if (rows.length){
          const safe = this.selectedPeriodKeys.join('_').replaceAll('/', '-').replaceAll('\\','-').replaceAll(':','-').replaceAll(' - ', '--');
          pdfMake.createPdf(doc).download(`Zestawienie_produktów_${safe}.pdf`);
          setTimeout(()=>this.close(), 500);
        } else {
          alert('Brak danych w wybranych okresach.');
        }
      } catch (e) {
        console.error(e);
        alert('Wystąpił błąd podczas generowania zestawienia.');
      } finally {
        this.isGenerating = false;
      }
    },

    tableLayout(){
      return {
        defaultBorder:false,
        hLineWidth:()=>1, vLineWidth:()=>1,
        hLineColor:(i)=>(i===0||i===1)?'#bfdde8':'#eaeaea',
        vLineColor:()=>'#eaeaea',
        paddingLeft:()=>10, paddingRight:()=>10, paddingTop:()=>2, paddingBottom:()=>2,
        fillColor:()=>'#fff'
      };
    },

    close(){ this.$emit("close"); this.detachObserver(); this.open=false; }
  },
  mounted(){
    this.loadFirstPage().then(()=>this.attachObserver());
  },
  beforeUnmount(){ this.detachObserver(); }
}
</script>

<style scoped></style>
